// /src/data/accountService.js
// Closing your own account, and coming back (members-can-leave, Roadmap #82;
// docs/briefs/2026-09-28-members-can-leave.md, addenda 1–4).
//
// Closing has two stages (migration 20260928g). request_account_closure() hides the member at once
// and starts a 30-day way back; cancel_account_closure() is the way back; the deletion itself runs
// later on the scheduler (Edge Function process-account-closure), never from the browser.
// The summary and the deletion share one definition of what goes (account_closure_content), so the
// summary shown is exactly what would be deleted.

import { supabaseFetch } from '../lib/supabase';

/**
 * What closing the signed-in member's account would delete, the groups that block it, and when the
 * way back would end if they closed now.
 * @returns {Promise<{data: {articles:number, events:number, comments:number, messages:number,
 *   others_items:number, blocking_groups:string[], deletes_on:string}|null, error: Object|null}>}
 */
export async function getAccountClosureSummary() {
  return supabaseFetch.rpc('get_account_closure_summary', {});
}

/**
 * Start closing the signed-in member's account: everything they published is hidden at once, and
 * deleted after delete_after unless they come back.
 * @returns {Promise<{data: {delete_after:string}|null, error: Object|null}>}
 */
export async function requestAccountClosure() {
  return supabaseFetch.rpc('request_account_closure', {});
}

/**
 * Come back: restore the signed-in member's content and profile, before delete_after.
 * @returns {Promise<{data: {posts_restored:number, groups_restored:number}|null, error: Object|null}>}
 */
export async function cancelAccountClosure() {
  return supabaseFetch.rpc('cancel_account_closure', {});
}

/**
 * Whether the signed-in member is closing their account.
 * @returns {Promise<{data: {closing:boolean, delete_after?:string, can_return?:boolean}|null,
 *   error: Object|null}>}
 */
export async function getAccountClosureStatus() {
  return supabaseFetch.rpc('get_account_closure_status', {});
}

/**
 * Why a closing call was refused, from the database's message. Each refusal carries a stable token;
 * the owned-groups one also carries the group names ("closure_owned_groups: A, B").
 * @param {Object} error
 * @returns {{reason: 'owned_groups', groups: string}
 *   | {reason: 'too_late' | 'not_closing' | 'already_closing' | 'unknown'}}
 */
export function closureRefusal(error) {
  // supabaseFetch.rpc puts PostgREST's whole JSON body in error.message; read its own message.
  let message = error?.message || '';
  try {
    const body = JSON.parse(message);
    if (typeof body?.message === 'string') message = body.message;
  } catch {
    // not JSON: use the text as it is
  }
  const match = /^closure_owned_groups:\s*(.+)$/s.exec(message);
  if (match) return { reason: 'owned_groups', groups: match[1].trim() };
  if (message === 'account_closure_too_late') return { reason: 'too_late' };
  if (message === 'account_not_closing') return { reason: 'not_closing' };
  if (message === 'account_already_closing') return { reason: 'already_closing' };
  return { reason: 'unknown' };
}

/**
 * The last moment of the way back, as the member reads it: their own time zone, in the UI language.
 * @param {string} iso - delete_after or deletes_on from the database
 * @param {string} locale - the formatting locale from useTranslation() (formattingLocale)
 * @returns {string}
 */
export function formatClosureDeadline(iso, locale) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(locale || undefined, { dateStyle: 'long', timeStyle: 'short' });
  } catch {
    return date.toLocaleString();
  }
}

// ---------------------------------------------------------------------------------------------
// Setting up a new account (Roadmap #88, registration confirm-first;
// docs/briefs/2026-10-03-registration-confirm-first.md + addendum 1). After confirming their email
// a member with no home area goes through AccountSetup (location, area, optional profile).
// ---------------------------------------------------------------------------------------------

/**
 * Whether the signed-in member has a home area — the one question the setup gate asks.
 * More than one home counts as having one (the gate is not the place to repair that).
 * @param {string} userId
 * @returns {Promise<{data: boolean|null, error: Object|null}>} data null on an error (the caller
 *   then shows nothing: a failed read must not put a member who has a home behind setup).
 */
export async function hasHomeArea(userId) {
  if (!userId) return { data: null, error: new Error('no user') };
  const { data, error } = await supabaseFetch.query('user_areas', {
    schema: 'public',
    select: 'id',
    eq: { user_id: userId, is_home: true },
    limit: 1
  });
  if (error) return { data: null, error };
  return { data: Array.isArray(data) && data.length > 0, error: null };
}

/**
 * Save the member's own About me and/or picture (setup's optional last step). Only the fields
 * given are written. users' own-row UPDATE policy is the authority.
 * @param {string} userId
 * @param {{bio?: string, avatar_url?: string}} fields
 * @returns {Promise<{error: Object|null}>}
 */
export async function updateOwnProfile(userId, fields) {
  const patch = {};
  if (typeof fields?.bio === 'string') patch.bio = fields.bio;
  if (typeof fields?.avatar_url === 'string') patch.avatar_url = fields.avatar_url;
  if (!userId || Object.keys(patch).length === 0) return { error: null };
  const authToken = await supabaseFetch.getAuthToken();
  // schema 'public': the member's own account even while a demo is being viewed (users is demo-routed).
  const { error } = await supabaseFetch.update('users', patch, { eq: { id: userId }, authToken, schema: 'public' });
  return { error: error || null };
}
